/**
 * Lane groups in the timeline editor.
 *
 * Operator, 2026-10-03: "group the lighting lanes", then "i need to be able to
 * separate individual lights into another group so i can have all but 1 on a
 * master control and have a light or lights doing something different"
 * (DEC-251, which replaced DEC-250's sub-groups by connection type).
 *
 * The shape:
 *  - Every lights lane sits in **Lighting**. Lighting itself is the main
 *    group: its masters, then the lights directly in it.
 *  - The operator's own groups (`lane.lightGroup`, a name) are Lighting's
 *    sub-groups, each with its own masters and lights. A master takes over
 *    only the lights in its own group (`lightMix.ts::masterReaches`).
 *  - Lanes that are not lights stay at the top level.
 *
 * A group exists while a lane names it: there is no group registry, so an
 * empty group cannot linger (the same rule as the rig's group tags in
 * `lighting/targets.ts::allGroups`). Group order is the order of each group's
 * first lane in the stored list.
 *
 * The stored lane array keeps whatever order it has. The editor draws
 * `orderedLanes`, and any move writes the lanes back IN that order, so the
 * stored order converges on the drawn one through the operator's own edits,
 * never by a silent rewrite.
 */

import type { TimelineLane } from '@opbuttons/schema';
import { lightGroupOf } from './lightMix.ts';

export const LIGHTING = 'lighting';
const SUB = `${LIGHTING}/`;

/** The key of a light group's header: `lighting/<name>`. */
export function groupKeyOf(name: string): string {
  return name ? `${SUB}${name}` : LIGHTING;
}
/** The light group a key names, `''` for Lighting itself. */
export function groupOfKey(key: string): string {
  return key.startsWith(SUB) ? key.slice(SUB.length) : '';
}
export function groupName(key: string): string {
  return key === LIGHTING ? 'Lighting' : groupOfKey(key);
}

export function isLightLane(l: TimelineLane): boolean {
  const k = l.laneKind ?? 'mixed';
  return k === 'dmx-rig' || k === 'dmx-fixture';
}
function isMaster(l: TimelineLane): boolean {
  return (l.laneKind ?? 'mixed') === 'dmx-rig';
}

/** The group a lane sits DIRECTLY in: `''` for the top level, `'lighting'`,
 *  or `'lighting/<name>'`. */
export function laneParent(l: TimelineLane): string {
  return isLightLane(l) ? groupKeyOf(lightGroupOf(l)) : '';
}

/** The light groups the operator has made, in order of their first lane. */
export function lightGroupsOf(lanes: readonly TimelineLane[]): string[] {
  const seen: string[] = [];
  for (const l of lanes) {
    const g = isLightLane(l) ? lightGroupOf(l) : '';
    if (g && !seen.includes(g)) seen.push(g);
  }
  return seen;
}

/** A name for a new group that no lane uses yet: "Group 2", "Group 3"... */
export function freeGroupName(lanes: readonly TimelineLane[]): string {
  const used = new Set(lightGroupsOf(lanes).map((g) => g.toLowerCase()));
  for (let n = 2; ; n++) if (!used.has(`group ${n}`)) return `Group ${n}`;
}

/** A node of the lane tree: a lane, or a group holding nodes. */
export type LaneNode = { lane: TimelineLane } | { group: string; children: LaneNode[] };

/** The tree the editor draws. Lighting sits where the first light lane sits;
 *  inside any light group the masters come first, then its lights; Lighting's
 *  sub-groups follow its own lights. Lanes keep their stored order. */
export function laneTree(lanes: readonly TimelineLane[]): LaneNode[] {
  const root: LaneNode[] = [];
  let lighting: { group: string; children: LaneNode[] } | null = null;
  const members = new Map<string, { masters: LaneNode[]; lights: LaneNode[] }>();
  for (const lane of lanes) {
    if (!isLightLane(lane)) { root.push({ lane }); continue; }
    if (!lighting) { lighting = { group: LIGHTING, children: [] }; root.push(lighting); }
    const g = lightGroupOf(lane);
    let m = members.get(g);
    if (!m) { m = { masters: [], lights: [] }; members.set(g, m); }
    (isMaster(lane) ? m.masters : m.lights).push({ lane });
  }
  if (lighting) {
    const own = members.get('') ?? { masters: [], lights: [] };
    lighting.children = [
      ...own.masters, ...own.lights,
      ...lightGroupsOf(lanes).map((g) => ({ group: groupKeyOf(g), children: [...members.get(g)!.masters, ...members.get(g)!.lights] })),
    ];
  }
  return root;
}

function flatten(nodes: readonly LaneNode[], out: TimelineLane[] = []): TimelineLane[] {
  for (const n of nodes) {
    if ('lane' in n) out.push(n.lane);
    else flatten(n.children, out);
  }
  return out;
}

/** Every lane, in the order the editor draws them. */
export function orderedLanes(lanes: readonly TimelineLane[]): TimelineLane[] {
  return flatten(laneTree(lanes));
}

/** The lanes inside a group, its sub-groups' included, in drawn order. */
export function lanesInGroup(lanes: readonly TimelineLane[], key: string): TimelineLane[] {
  const find = (nodes: readonly LaneNode[]): LaneNode | null => {
    for (const n of nodes) {
      if ('group' in n) {
        if (n.group === key) return n;
        const hit = find(n.children);
        if (hit) return hit;
      }
    }
    return null;
  };
  const g = find(laneTree(lanes));
  return g && 'group' in g ? flatten(g.children) : [];
}

/** What the editor draws, row by row: a group's header, then (unless it is
 *  folded) what is inside it. `depth` is how far in the row sits. */
export type LaneRow =
  | { type: 'lane'; lane: TimelineLane; depth: number }
  | { type: 'group'; key: string; depth: number };

export function laneRows(lanes: readonly TimelineLane[], folded: (key: string) => boolean): LaneRow[] {
  const rows: LaneRow[] = [];
  const walk = (nodes: readonly LaneNode[], depth: number) => {
    for (const n of nodes) {
      if ('lane' in n) { rows.push({ type: 'lane', lane: n.lane, depth }); continue; }
      rows.push({ type: 'group', key: n.group, depth });
      if (!folded(n.group)) walk(n.children, depth + 1);
    }
  };
  walk(laneTree(lanes), 0);
  return rows;
}

const nodeId = (n: LaneNode) => ('lane' in n ? n.lane.id : `group:${n.group}`);
/** Inside a light group, masters, lights and sub-groups are three runs that
 *  do not mix (that is how they are drawn); the top level is one run. */
const runOf = (n: LaneNode, inLights: boolean) => (!inLights ? 'x' : 'group' in n ? 'g' : isMaster(n.lane) ? 'm' : 'l');

/**
 * Move lanes, or one group, up or down one place among their siblings, and
 * return the whole lane list in its new drawn order; null when the move
 * cannot happen (the ends of a run, or a selection that spans groups).
 *
 * `ids` are lane ids, or `group:<key>` for a group. All must share a parent.
 * A top-level lane moving past Lighting jumps the whole group, one place.
 */
export function moveInTree(lanes: readonly TimelineLane[], ids: readonly string[], d: -1 | 1): TimelineLane[] | null {
  if (ids.length === 0) return null;
  const tree = laneTree(lanes);
  const want = new Set(ids);
  const siblingsOf = (nodes: LaneNode[]): LaneNode[] | null => {
    if (nodes.some((n) => want.has(nodeId(n)))) return nodes;
    for (const n of nodes) {
      if ('group' in n) {
        const hit = siblingsOf(n.children);
        if (hit) return hit;
      }
    }
    return null;
  };
  const sibs = siblingsOf(tree);
  if (!sibs) return null;
  const idx = sibs.map((n, i) => (want.has(nodeId(n)) ? i : -1)).filter((i) => i >= 0);
  if (idx.length !== want.size) return null; // the selection spans groups
  const inLights = sibs !== tree;
  const run = runOf(sibs[idx[0]!]!, inLights);
  if (idx.some((i) => runOf(sibs[i]!, inLights) !== run)) return null;
  const order = d < 0 ? idx : [...idx].reverse();
  const next = [...sibs];
  for (const i of order) {
    const j = i + d;
    if (j < 0 || j >= next.length || runOf(next[j]!, inLights) !== run || want.has(nodeId(next[j]!))) return null;
    [next[i], next[j]] = [next[j]!, next[i]!];
  }
  sibs.splice(0, sibs.length, ...next);
  return flatten(tree);
}

/**
 * Put lights lanes into a light group (`''` = Lighting itself), in one edit,
 * and return the whole lane list. With `before`, the lanes land just before
 * that lane in the stored order (a drop between two lanes); otherwise they
 * keep their places. Lanes that are not lights are left alone.
 */
export function setLightGroup(lanes: readonly TimelineLane[], ids: readonly string[], group: string, before?: string): TimelineLane[] {
  const name = group.trim();
  const want = new Set(ids);
  const moved = (l: TimelineLane): TimelineLane => {
    if (!want.has(l.id) || !isLightLane(l)) return l;
    const { lightGroup: _old, ...rest } = l;
    void _old;
    return name ? { ...rest, lightGroup: name } : rest;
  };
  if (!before || want.has(before)) return lanes.map(moved);
  // From the DRAWN order, as a move does: taken from the stored order, a lane
  // lifted out of an early slot would carry Lighting's place with it (the
  // group is drawn where its first lane is stored) and the whole group would
  // jump past the lanes around it.
  const drawn = orderedLanes(lanes);
  const going = drawn.filter((l) => want.has(l.id) && isLightLane(l)).map(moved);
  const staying = drawn.filter((l) => !(want.has(l.id) && isLightLane(l)));
  const at = staying.findIndex((l) => l.id === before);
  if (at < 0) return lanes.map(moved);
  return [...staying.slice(0, at), ...going, ...staying.slice(at)];
}

/** Rename a light group: every lane in it, in one edit. A name another group
 *  already uses merges the two, which is what moving into it would do. */
export function renameLightGroup(lanes: readonly TimelineLane[], from: string, to: string): TimelineLane[] {
  const name = to.trim();
  if (!from || !name || name === from) return [...lanes];
  return lanes.map((l) => (isLightLane(l) && lightGroupOf(l) === from ? { ...l, lightGroup: name } : l));
}
